import { useAppSelector } from "hooks/redux";
import { BuilderMediaGalleryPanel } from "pages/secure/facility/builders/shared/components/mediaGallery/BuilderMediaGalleryPanel";
import { WebsiteBuilderHelpIcon } from "pages/secure/facility/websiteBuilder/editor/components/helpIcon/WebsiteBuilderHelpIcon";
import { WebsiteBuilderMediaDropZone } from "pages/secure/facility/websiteBuilder/editor/components/mediaDropZone/WebsiteBuilderMediaDropZone";
import { IWebsiteBuilderSlideshowImage } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import { isWebsiteBuilderImageAllowedFileSubtype } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderAllowedMedia";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";

interface ISlideshowImagesReplaceViewProps {
  images: IWebsiteBuilderSlideshowImage[];
  mediaId: number;
  selectedImage: IWebsiteBuilderSlideshowImage;
  setSelectedImage: (selectedImage: IWebsiteBuilderSlideshowImage) => void;
}

export function SlideshowImagesReplaceView(props: ISlideshowImagesReplaceViewProps) {
  const { images, mediaId, selectedImage, setSelectedImage } = props;

  const { t } = useTranslation();

  const media = useAppSelector(store => store.websiteBuilderStore.editor.media);
  const stockMedia = useAppSelector(store => store.websiteBuilderStore.editor.stockMedia);

  const [alt, setAlt] = useState<string>(selectedImage.alt);

  const mediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return media
      ?.filter(mediaItem =>
        isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype) &&
        mediaItem.archived_at == null &&
        (mediaItem.id === mediaId || images.every(image => image.mediaId !== mediaItem.id)),
      ) ?? [];
  }, [media, mediaId, images]);

  const stockMediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return stockMedia
      ?.filter(mediaItem =>
        isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype) &&
        (mediaItem.id === mediaId || images.every(image => image.mediaId !== mediaItem.id)),
      ) ?? [];
  }, [stockMedia, mediaId, images]);

  function selectMediaItem(mediaItem: IWebsiteBuilderMediaItem) {
    const image: IWebsiteBuilderSlideshowImage = {
      mediaId: mediaItem.id,
      src: mediaItem.source,
      alt,
    };

    setSelectedImage(image);
  }

  function handleAltChange(nextAlt: string) {
    setAlt(nextAlt);

    setSelectedImage({
      ...selectedImage,
      alt: nextAlt,
    });
  }

  return (
    <BuilderMediaGalleryPanel
      myMediaItems={mediaOptions}
      stockMediaItems={stockMediaOptions}
      selectedMediaId={selectedImage.mediaId}
      onSelectMedia={selectMediaItem}
      uploadSlot={
        <WebsiteBuilderMediaDropZone
          theme="dark"
          condensed
          allowImages
        />
      }
      alt={alt}
      onAltChange={handleAltChange}
      altInputSuffix={
        <WebsiteBuilderHelpIcon
          id="website-builder-slideshow-images-replace-alt-text-help"
          message={t("Improves accessibility and helps search engines understand the image.")}
          placement="left"
        />
      }
    />
  );
}
